import _ from 'lodash';
import type { SortDirection, ValueGetterParams } from 'ag-grid-community';

import captureException from 'services/captureException';
import toSentenceCase from 'services/toSentenceCase';
import isCustomRangeInput from 'isCustomRangeInput';
import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';
import limitModeTypeCheckers from 'components/V5GadgetForm/limitModeTypeCheckers';
import { getComparisonLabel } from 'components/Report/ComparisonSelector/ComparisonSelector';
import formatDateLabel from 'components/V5Gadget/formatDateLabel';
import getMatrixDefaultSort from 'components/V5Gadget/Matrix/getMatrixDefaultSort';
import DimensionAHeaderCell from 'components/V5Gadget/Matrix/DimensionAHeaderCell';
import { DELTA_HEADER_CELL } from 'components/Grid/DeltaHeaderCell';
import {
  MATRIX_BOLD_CELL_CLASS,
  MATRIX_COMPARISON_VALUE_HEADER_CLASS,
} from 'components/Grid/StyleOverrides';
import { FleetOpsCellRendererParams } from 'types/agGrid';

import {
  canShowRankAndPercentage,
  getPercentageOfTotalField,
  getRankField,
} from './addRankAndPercentageOfTotal';
import { getFieldView } from './getFieldView';
import getFieldName from './getFieldName';
import getReversedGroupingField from './getReversedGroupingField';
import shouldLeftAlignMetric from './shouldLeftAlignMetric';
import deltaSort from './sortFunctions/deltaSort';
import percentDeltaSort from './sortFunctions/percentDeltaSort';
import getOrderedDaysOfWeek from '../getOrderedDaysOfWeek';
import getOrderedMonths from '../getOrderedMonths';

// ---------------------------------------------------------------------------
// Shared args that flow through all col-def builders
// ---------------------------------------------------------------------------

interface BuildColDefSharedArgs {
  chartDef: V5ChartDefinition;
  baseViews: FleetOps.BaseView[];
  currentDateScope: DateRangeInput;
  previousDateScope: DateRangeInput;
  comparison: PersistedComparisonType | undefined;
  currentSortBy: string | undefined;
  defaultCompareSort: string | undefined;
  compareSortType: MatrixCompareSortType | undefined;
  isSortAbsolute: boolean | undefined;
  overrideSortField: string | undefined;
  overrideSortDirection: SortDirection | undefined;
  getEntityAndDefaultAppForField: ({
    field,
  }: {
    field: string;
  }) => { entity: EntityDetails.Entity; app: EntityDetails.App } | undefined;
  cellComparator: (a: any, b: any, nodeA?: any, nodeB?: any) => number;
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

const buildColDef = <TData = any, TValue = any>(
  shared: BuildColDefSharedArgs,
  {
    headerName,
    headerTooltip,
    field,
    comparator,
    isLeftAlign,
    isGroupingCell,
    isStatusFlag,
    metric,
    isComparisonCurrentValue,
    colourConfig,
  }: {
    headerName?: string;
    headerTooltip?: string;
    field: string;
    comparator?: (a: any, b: any, nodeA?: any, nodeB?: any) => number;
    isLeftAlign?: boolean;
    isGroupingCell?: boolean;
    isStatusFlag?: boolean;
    metric?: Metrics.Metric;
    isComparisonCurrentValue?: boolean;
    colourConfig?: FleetOps.BonusScoreColourConfig;
  },
) => {
  const {
    chartDef,
    baseViews,
    currentDateScope,
    previousDateScope,
    currentSortBy,
    defaultCompareSort,
    compareSortType,
    isSortAbsolute,
    overrideSortField,
    overrideSortDirection,
    getEntityAndDefaultAppForField,
  } = shared;

  const cellRendererParams: FleetOpsCellRendererParams = {
    isLeftAlign: !!isLeftAlign,
    fieldView: getFieldView(metric, baseViews),
    metric,
    currentDateScope,
    previousDateScope,
    entityDefinition: getEntityAndDefaultAppForField({ field }),
    colourConfig,
  };

  return {
    headerName: headerName ? headerName : field,
    headerTooltip: metric ? undefined : headerTooltip ? headerTooltip : field,
    field,
    comparator,
    cellRenderer: isGroupingCell
      ? 'groupingCellRenderer'
      : isStatusFlag
        ? 'statusFlagCell'
        : 'normalCellRenderer',
    cellRendererParams,
    headerClass: isLeftAlign ? undefined : 'rightAlignedHeader',
    suppressHeaderMenuButton: true,
    suppressMovable: isGroupingCell,
    wrapHeaderText: true,
    sortingOrder: ['desc', 'asc', null],
    autoHeaderHeight: true,
    flex: chartDef.gridSizing === 'fitContent' ? undefined : 1,
    valueGetter: (params: ValueGetterParams<TData, TValue>) => {
      if (params.data) {
        // ts-ignore should be avoided wherever possible.
        // However - This feature has bigger problems
        // @ts-ignore
        return params.data[field];
      }
      return undefined;
    },
    headerComponentParams: metric
      ? {
          metric,
          rightAlign: !isLeftAlign && !shouldLeftAlignMetric(metric),
          alias: headerName,
          sort:
            metric.id === currentSortBy ? chartDef.groupBySortBy : undefined,
          isComparisonCurrentValue,
        }
      : { isComparisonCurrentValue },
    sort: (() => {
      if (overrideSortField && isSortAbsolute !== undefined) {
        return field === overrideSortField ? overrideSortDirection : undefined;
      }
      if (chartDef.grouping?.defaultSort) {
        const defaultField = chartDef.grouping?.defaultSort.field;

        if (defaultField === field) {
          return chartDef.grouping?.defaultSort.direction;
        } else return undefined;
      }
      if (
        limitModeTypeCheckers.isExcludeTopN(chartDef.groupByLimitMode) &&
        field === chartDef.groupByLimitMode.displaySort?.field
      ) {
        return chartDef.groupByLimitMode.displaySort.direction;
      }
      if (currentSortBy && metric && metric.id === currentSortBy) {
        return chartDef.groupBySortBy;
      }
      if (isGroupingCell && !currentSortBy) {
        return chartDef.groupBySortBy;
      }
      if (field === defaultCompareSort && compareSortType) {
        return compareSortType;
      }
    })(),
  };
};

const buildDeltaColDef = (
  shared: BuildColDefSharedArgs,
  metricId: string,
  comparison?: PersistedComparisonType,
) => {
  const { chartDef, isSortAbsolute, overrideSortField } = shared;

  return {
    ...buildColDef(shared, {
      headerName: 'Change',
      isLeftAlign: true,
      field: `${metricId}-delta`,
      headerTooltip: `The difference between the current period and the ${getComparisonLabel(
        comparison,
      ).toLowerCase()}`,
      // @ts-ignores
      comparator: (a, b) => {
        if (!a || !b) {
          return -1;
        }
        let isIgnoreWinningOrLosing: boolean;
        if (overrideSortField && isSortAbsolute !== undefined) {
          isIgnoreWinningOrLosing = isSortAbsolute;
        } else if (chartDef.grouping?.defaultSort) {
          isIgnoreWinningOrLosing =
            chartDef.grouping?.defaultSort.isAbsolute || false;
        } else if (
          limitModeTypeCheckers.isExcludeTopN(chartDef.groupByLimitMode)
        ) {
          isIgnoreWinningOrLosing =
            chartDef.groupByLimitMode.displaySort?.isAbsolute || false;
        } else {
          const sortArgs = getMatrixDefaultSort(chartDef);
          const defaultSortMode = sortArgs ? sortArgs.defaultSortMode : 'abs';
          isIgnoreWinningOrLosing = defaultSortMode.includes('abs');
        }

        return deltaSort(isIgnoreWinningOrLosing, a, b);
      },
    }),
    headerComponent: DELTA_HEADER_CELL,
    headerComponentParams: {
      title: 'Change',
      defaultSortField: chartDef.grouping?.defaultSort?.field,
    },
  };
};

const buildPercentDeltaColDef = (
  shared: BuildColDefSharedArgs,
  metricId: string,
) => {
  const { chartDef } = shared;

  return {
    ...buildColDef(shared, {
      headerName: '%Chg',
      isLeftAlign: true,
      field: `${metricId}-percentDelta`,
      headerTooltip: `The percentage change between the current period and the ${getComparisonLabel(
        shared.comparison,
      ).toLowerCase()}`,
      // @ts-ignore
      comparator: (a, b) => {
        if (!a || !b) {
          return -1;
        }
        let isIgnoreWinningOrLosing: boolean;
        if (chartDef.grouping?.defaultSort || chartDef.grouping?.currentSort) {
          isIgnoreWinningOrLosing =
            chartDef.grouping?.currentSort?.isAbsolute ||
            chartDef.grouping.defaultSort.isAbsolute ||
            false;
        } else if (
          limitModeTypeCheckers.isExcludeTopN(chartDef.groupByLimitMode)
        ) {
          isIgnoreWinningOrLosing =
            chartDef.groupByLimitMode.displaySort?.isAbsolute || false;
        } else {
          const sortArgs = getMatrixDefaultSort(chartDef);
          if (!sortArgs) {
            return 0;
          }
          const { defaultSortMode } = sortArgs;
          isIgnoreWinningOrLosing = defaultSortMode.includes('abs');
        }

        return percentDeltaSort(isIgnoreWinningOrLosing, a, b);
      },
    }),
    headerComponent: DELTA_HEADER_CELL,
    headerComponentParams: {
      title: '%Chg',
      defaultSortField: chartDef.grouping?.defaultSort?.field,
    },
  };
};

// A series' rank and % of total columns, beside its own (see
// addRankAndPercentageOfTotal)
const buildRankAndPercentageColDefs = (
  shared: BuildColDefSharedArgs,
  { series, name }: { series: V5ChartDefinitionSeries; name: string },
) => {
  if (!canShowRankAndPercentage(shared.chartDef)) {
    return [];
  }
  const rankColDef = {
    ...buildColDef(shared, {
      field: getRankField(series.metricId),
      headerName: `${name} (Rank)`,
      headerTooltip: `${name} (Rank)`,
      comparator: shared.cellComparator,
    }),
    // Best first
    sortingOrder: ['asc', 'desc', null],
  };
  const percentageColDef = buildColDef(shared, {
    field: getPercentageOfTotalField(series.metricId),
    headerName: `${name} (% of total)`,
    headerTooltip: `${name} as a percentage of the total`,
    comparator: shared.cellComparator,
  });
  return [
    ...(series.isRankingEnabled ? [rankColDef] : []),
    ...(series.isPercentageOfTotalEnabled ? [percentageColDef] : []),
  ];
};

const buildComparisonValueColDef = (
  shared: BuildColDefSharedArgs,
  metricId: string,
  comparison?: PersistedComparisonType,
) => {
  const colDef = buildColDef(shared, {
    // e.g. "Previous Period", "Same Period - Previous Year"
    headerName: getComparisonLabel(comparison),
    headerTooltip: `The actual value for the ${getComparisonLabel(
      comparison,
    ).toLowerCase()}`,
    field: `${metricId}-comparisonValue`,
    comparator: shared.cellComparator,
    // Obfuscate by value (matching the current-value column) rather than by
    // the synthetic `${metricId}-comparisonValue` field name.
    isComparisonCurrentValue: true,
  });

  // The comparison-value column sits between the current value and the delta
  // columns, so give its header an explicit right border to separate it from
  // the delta columns (the current value to its left already borders it),
  // rather than relying on ag-grid's positional border rules.
  const headerClass = [colDef.headerClass, MATRIX_COMPARISON_VALUE_HEADER_CLASS]
    .filter(Boolean)
    .join(' ');
  return { ...colDef, headerClass };
};

/**
 * Extract unique dimensionA values from responses, respecting day/month
 * ordering. In reversed-axes mode these values become the matrix columns, so
 * when the gadget is sorted by the dimension (not a metric) we order them here
 * client-side - ag-grid cannot reorder columns via a header click, and the
 * query response is ordered by the metric, not the dimension.
 */
export const extractDimensionAValues = ({
  chartDef,
  responses,
  groupByField,
  weekStartsOn,
  cellComparator,
}: {
  chartDef: V5ChartDefinition;
  responses: MetricsResponse[];
  groupByField: string;
  weekStartsOn: WeekStartsOn;
  cellComparator: (a: any, b: any, nodeA?: any, nodeB?: any) => number;
}): string[] => {
  if (!chartDef.dimensionA || !responses[0]) {
    return [];
  }

  const values = (responses[0] as any[]).map((r) => r[groupByField]);
  const uniqueValues = _.uniq(values).filter(
    (v): v is string => v !== undefined && v !== null,
  );

  // Is the gadget sorted by the dimension itself (rather than a metric)?
  const defaultSort = chartDef.grouping?.defaultSort;
  const isDimensionSort =
    defaultSort?.field === chartDef.dimensionA.field && !defaultSort?.metricId;
  const isDescending =
    (defaultSort?.direction ?? chartDef.groupBySortBy ?? 'desc') !== 'asc';
  const applyDirection = (arr: string[]): string[] =>
    isDescending ? [...arr].reverse() : arr;

  if (chartDef.dimensionA.isGroupByDayOfWeek) {
    const orderedDays = getOrderedDaysOfWeek(weekStartsOn);
    const canonical = orderedDays.filter((day) => uniqueValues.includes(day));
    return isDimensionSort ? applyDirection(canonical) : canonical;
  }

  if (chartDef.dimensionA.isGroupByMonth) {
    const orderedMonths = getOrderedMonths();
    const canonical = orderedMonths.filter((month) =>
      uniqueValues.includes(month),
    );
    return isDimensionSort ? applyDirection(canonical) : canonical;
  }

  if (isDimensionSort) {
    const sorted = [...uniqueValues].sort((a, b) => cellComparator(a, b));
    return applyDirection(sorted);
  }

  return uniqueValues;
};

/** Build the reversed-axes column set (shared by 1D and 2D reversed cases). */
const buildReversedColDefs = (
  shared: BuildColDefSharedArgs,
  {
    groupingField,
    groupingHeaderName,
    dimensionAValues,
    chartDef,
    groupComparator,
  }: {
    groupingField: string;
    groupingHeaderName: string;
    dimensionAValues: string[];
    chartDef: V5ChartDefinition;
    groupComparator: (a: any, b: any) => number;
  },
): MatrixColDef[] => {
  const {
    cellComparator,
    currentDateScope,
    previousDateScope,
    isSortAbsolute,
    overrideSortField,
    overrideSortDirection,
  } = shared;

  // Only sort the reversed grouping column (the metric-name column in 1D, or the
  // dimensionB column in 2D) when the user explicitly targets it - either via a
  // live header-click override or a persisted default sort on that field. We do
  // NOT fall back to groupBySortBy here: that direction belongs to dimensionA
  // (now the columns), and applying it would clobber the configured row order
  // (e.g. reorder the metric rows alphabetically on load).
  const groupingSort: SortDirection | undefined = (() => {
    if (overrideSortField && isSortAbsolute !== undefined) {
      return groupingField === overrideSortField
        ? overrideSortDirection
        : undefined;
    }
    if (chartDef.grouping?.defaultSort?.field === groupingField) {
      return chartDef.grouping.defaultSort.direction;
    }
    return undefined;
  })();

  // In a reversed matrix the total lives in a pinned-right column (not the
  // pinned-bottom row that `.ag-floating-bottom-container` bolds), and the
  // left column holds the metric names. Bold both to emphasise them the same
  // way the normal-axes total row is emphasised. We use a cell CLASS (styled in
  // StyleOverrides to target the cell's descendants) rather than a `cellStyle`
  // font-weight: the latter sets weight on the outer `.ag-cell` only and does
  // not reach the inner text elements, which carry their own weight.
  const isMetricColumn = groupingField === 'metric';

  const reversedGroupDef = {
    headerName: groupingHeaderName,
    headerTooltip: groupingHeaderName,
    field: groupingField,
    comparator: groupComparator,
    cellRenderer: 'normalCellRenderer',
    cellRendererParams: {
      isLeftAlign: true,
      currentDateScope,
      previousDateScope,
    },
    cellClass: isMetricColumn ? MATRIX_BOLD_CELL_CLASS : undefined,
    headerClass: undefined,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    wrapHeaderText: true,
    sortingOrder: ['desc', 'asc', null],
    sort: groupingSort,
    autoHeaderHeight: true,
    flex: chartDef.gridSizing === 'fitContent' ? undefined : 1,
    valueGetter: (params: any) => params.data?.[groupingField],
    headerComponentParams: {},
  } as MatrixColDef;

  const totalsColDef = buildColDef(shared, {
    field: 'total',
    headerName: 'Total',
    headerTooltip: 'Total',
    comparator: cellComparator,
    isLeftAlign: false,
  });

  const isDateDimension = chartDef.dimensionA?.fieldType === 'date';
  const interval = chartDef.trendByCalendarInterval;

  const dataColumns = dimensionAValues.map((value) => ({
    ...buildColDef(shared, {
      field: value,
      headerName: value,
      headerTooltip: value,
      comparator: cellComparator,
    }),
    headerComponent: DimensionAHeaderCell,
    headerComponentParams: {
      dimensionValue: value,
      isDateDimension,
      interval: isDateDimension ? interval : undefined,
    },
    sortable: false,
    suppressHeaderMenuButton: true,
  }));

  // 'last' (default) pins the Total column rightmost; 'first' places it directly
  // after the label/grouping column, sizing like a normal data column.
  const totalsPosition = chartDef.totalsColumnPosition ?? 'last';

  if (totalsPosition === 'first') {
    return [
      reversedGroupDef,
      {
        ...totalsColDef,
        // Not pinned: it flows with the data columns and keeps buildColDef's
        // own flex sizing. Bold it like the pinned-right variant.
        cellClass: MATRIX_BOLD_CELL_CLASS,
      },
      ...dataColumns,
    ] as MatrixColDef[];
  }

  return [
    reversedGroupDef,
    ...dataColumns,
    {
      ...totalsColDef,
      pinned: 'right',
      // Pinned columns ignore `flex`; the width is set by auto-sizing the
      // 'total' column (see useMatrixContent -> additionalAutoSizeColumnIds).
      flex: undefined,
      cellClass: MATRIX_BOLD_CELL_CLASS,
    },
  ] as MatrixColDef[];
};

// ---------------------------------------------------------------------------
// Public export
// ---------------------------------------------------------------------------

interface BuildMatrixColDefsArgs extends BuildColDefSharedArgs {
  usedMetrics: Metrics.Metric[];
  isTwoDimensional: boolean;
  groupByField: string;
  groupComparator: (a: any, b: any) => number;
  rangeBuckets: string[];
  weekStartsOn: WeekStartsOn;
  responses: MetricsResponse[];
}

const buildMatrixColDefs = (args: BuildMatrixColDefsArgs): MatrixColDef[] => {
  const {
    chartDef,
    baseViews,
    usedMetrics,
    isTwoDimensional,
    groupByField,
    groupComparator,
    rangeBuckets,
    weekStartsOn,
    responses,
    comparison,
    cellComparator,
  } = args;

  const reverseAxes = chartDef.reverseAxes || false;

  // --- Reversed-axes path ---
  if (reverseAxes) {
    const dimensionAValues = extractDimensionAValues({
      chartDef,
      responses,
      groupByField,
      weekStartsOn,
      cellComparator,
    });

    if (isTwoDimensional && chartDef.dimensionA && chartDef.dimensionB) {
      const dimensionBField = getReversedGroupingField({
        chartDef,
        isTwoDimensional,
      });

      return buildReversedColDefs(args, {
        groupingField: dimensionBField,
        groupingHeaderName: toSentenceCase(dimensionBField, true),
        dimensionAValues,
        chartDef,
        groupComparator,
      });
    } else {
      return buildReversedColDefs(args, {
        groupingField: 'metric',
        groupingHeaderName: 'Metric',
        dimensionAValues,
        chartDef,
        groupComparator,
      });
    }
  }

  // --- Normal path ---

  const getDimensionHeaderName = (field: string) => {
    const baseView = baseViews.find((v) => {
      const f = v.fields[field];
      if (f && f.nameAlias) {
        return v;
      }
      return undefined;
    });

    if (baseView) {
      return getFieldName(field, baseView);
    }
    return toSentenceCase(field, true);
  };
  const groupDefHeaderName = getDimensionHeaderName(groupByField);

  const headerName = (() => {
    if (chartDef.trendByHourInterval && groupByField === 'key') {
      return 'Hour';
    }

    if (groupByField === 'date') {
      return 'date';
    }

    return groupDefHeaderName;
  })();

  const groupDef = buildColDef(args, {
    headerName,
    headerTooltip: toSentenceCase(groupByField),
    field: groupByField,
    comparator: groupComparator,
    isLeftAlign: true,
    isGroupingCell: true,
  });

  // Matrix secondary row field (e.g. Driver Code beside Truck). A plain
  // left-aligned column: drill-downs stay on the dimensionA grouping cell.
  const secondaryDimensionDefs =
    chartDef.secondaryDimensionA && isSecondaryDimensionAInEffect(chartDef)
      ? [
          buildColDef(args, {
            headerName: getDimensionHeaderName(
              chartDef.secondaryDimensionA.field,
            ),
            headerTooltip: toSentenceCase(chartDef.secondaryDimensionA.field),
            field: chartDef.secondaryDimensionA.field,
            comparator: cellComparator,
            isLeftAlign: true,
          }),
        ]
      : [];

  const isBDate = chartDef.dimensionB
    ? chartDef.dimensionB.fieldType === 'date'
    : false;

  if (isTwoDimensional && chartDef.dimensionA && chartDef.dimensionB) {
    const bRange = chartDef.dimensionB.rangeInput;
    // Every bucket cell in a 2D matrix is the first series' metric
    const colourConfig = chartDef.series[0]?.cellColourConfig;

    if (bRange && isCustomRangeInput(bRange)) {
      return [
        groupDef,
        ...rangeBuckets.map((bucketKey) =>
          buildColDef(args, {
            field: bucketKey,
            comparator: cellComparator,
            colourConfig,
          }),
        ),
      ] as MatrixColDef[];
    } else if (isBDate && chartDef.series.length === 1) {
      const s = chartDef.series[0];
      const metric = usedMetrics.find((m) => m.id === s.metricId);
      if (metric) {
        return [
          groupDef,
          ...secondaryDimensionDefs,
          ...rangeBuckets.map((bucketKey, index) => {
            const baseDef = buildColDef(args, {
              field: bucketKey,
              headerName: s.displayName ? s.displayName : metric.name,
              headerTooltip: s.displayName ? s.displayName : metric.name,
              comparator: cellComparator,
              isComparisonCurrentValue: true,
              colourConfig,
            });
            if (s.matrixCellType && comparison) {
              const children = (() => {
                if (index === 0) {
                  return [baseDef];
                }
                if (s.matrixCellType === 'both') {
                  return [
                    baseDef,
                    buildDeltaColDef(args, bucketKey, comparison),
                    buildPercentDeltaColDef(args, bucketKey),
                  ];
                } else if (s.matrixCellType === 'delta') {
                  return [
                    baseDef,
                    buildDeltaColDef(args, bucketKey, comparison),
                  ];
                } else {
                  return [baseDef, buildPercentDeltaColDef(args, bucketKey)];
                }
              })();
              return {
                headerName: formatDateLabel(
                  bucketKey,
                  chartDef.trendByCalendarInterval
                    ? chartDef.trendByCalendarInterval
                    : 'auto',
                  true,
                  undefined,
                  chartDef.gadgetType === 'matrix',
                  false,
                  true,
                ),
                headerTooltip: formatDateLabel(
                  bucketKey,
                  chartDef.trendByCalendarInterval
                    ? chartDef.trendByCalendarInterval
                    : 'auto',
                  true,
                  undefined,
                  chartDef.gadgetType === 'matrix',
                ),
                marryChildren: true,
                flex: chartDef.gridSizing === 'fitContent' ? undefined : 1,
                children,
              };
            } else {
              return {
                ...baseDef,
                flex: chartDef.gridSizing === 'fitContent' ? undefined : 1,
                headerName: formatDateLabel(
                  bucketKey,
                  chartDef.trendByCalendarInterval
                    ? chartDef.trendByCalendarInterval
                    : 'auto',
                  true,
                  undefined,
                  chartDef.gadgetType === 'matrix',
                  false,
                  true,
                ),
                headerTooltip: formatDateLabel(
                  bucketKey,
                  chartDef.trendByCalendarInterval
                    ? chartDef.trendByCalendarInterval
                    : 'auto',
                  true,
                  undefined,
                  chartDef.gadgetType === 'matrix',
                ),
              };
            }
          }),
        ] as MatrixColDef[];
      } else {
        return [];
      }
    } else if (chartDef.dimensionB.isGroupByDayOfWeek) {
      return [
        groupDef,
        ...getOrderedDaysOfWeek(weekStartsOn).map((dayOfWeek) => {
          return buildColDef(args, {
            field: dayOfWeek,
            headerName: dayOfWeek,
            headerTooltip: dayOfWeek,
            comparator: cellComparator,
            colourConfig,
          });
        }),
      ] as MatrixColDef[];
    } else if (chartDef.dimensionB.isGroupByMonth) {
      return [
        groupDef,
        ...getOrderedMonths().map((month) => {
          return buildColDef(args, {
            field: month,
            headerName: month,
            headerTooltip: month,
            comparator: cellComparator,
            colourConfig,
          });
        }),
      ] as MatrixColDef[];
    } else if (
      chartDef.dimensionB.fieldType === 'text' ||
      chartDef.dimensionB.fieldType === 'boolean'
    ) {
      // Categorical second dimension (terms x terms via subGroupBy): one column
      // per distinct dimensionB member found in the nested `values`.
      const subField = chartDef.dimensionB.field;
      const members = new Set<string>();
      (responses[0] as any[]).forEach((row) => {
        (row.values || []).forEach((v: { [key: string]: unknown }) => {
          const raw = v[subField];
          const member =
            raw === undefined || raw === null || raw === ''
              ? '(Blank)'
              : String(raw);
          members.add(member);
        });
      });
      return [
        groupDef,
        ...Array.from(members).map((member) =>
          buildColDef(args, {
            field: member,
            headerName: member,
            headerTooltip: member,
            comparator: cellComparator,
            colourConfig,
          }),
        ),
      ] as MatrixColDef[];
    } else {
      return [
        groupDef,
        ...rangeBuckets.map((bucketKey) => {
          // @ts-ignore
          const delta = rangeBuckets[1] - rangeBuckets[0];
          // @ts-ignore
          const name = `${bucketKey} - ${bucketKey + delta}`;
          return buildColDef(args, {
            field: bucketKey.toString(),
            headerName: name,
            headerTooltip: name,
            comparator: cellComparator,
            colourConfig,
          });
        }),
      ] as MatrixColDef[];
    }
  } else {
    return [
      groupDef,
      ...secondaryDimensionDefs,
      ...chartDef.series
        .filter((s) => {
          const mId = s.metricId;
          const metric = usedMetrics.find((m) => m.id === mId);
          if (!metric) {
            const e = new Error(`Metric ${mId} not found`);
            captureException(e);
          }
          return !!metric;
        })
        .flatMap((s) => {
          const mId = s.metricId;
          const metric = usedMetrics.find((m) => m.id === mId);
          if (!metric) {
            throw new Error(`Metric ${mId} not found`);
          }
          const rankAndPercentageColDefs = buildRankAndPercentageColDefs(args, {
            series: s,
            name: s.displayName ? s.displayName : metric.name,
          });

          const baseDef = buildColDef(args, {
            field: metric.id,
            headerName: s.displayName ? s.displayName : metric.name,
            headerTooltip: s.displayName ? s.displayName : metric.name,
            comparator: cellComparator,
            isStatusFlag: s.isStatusFlag,
            metric,
            isLeftAlign: shouldLeftAlignMetric(metric),
            colourConfig: s.cellColourConfig,
          });
          const showDeltaCols = !!s.matrixCellType && !!comparison;
          // The comparison-value column only shows alongside a cell comparison
          // (delta/percentDelta/both) - it's the value the delta measures from.
          const showComparisonValue =
            !!s.matrixShowComparisonValue && showDeltaCols;
          if (showComparisonValue || showDeltaCols) {
            const children = (() => {
              const comparisonValueCol = showComparisonValue
                ? [buildComparisonValueColDef(args, metric.id, comparison)]
                : [];
              if (s.matrixCellType === 'both') {
                return [
                  baseDef,
                  ...comparisonValueCol,
                  buildDeltaColDef(args, metric.id, comparison),
                  buildPercentDeltaColDef(args, metric.id),
                ];
              } else if (s.matrixCellType === 'delta') {
                return [
                  baseDef,
                  ...comparisonValueCol,
                  buildDeltaColDef(args, metric.id, comparison),
                ];
              } else if (s.matrixCellType === 'percentDelta') {
                return [
                  baseDef,
                  ...comparisonValueCol,
                  buildPercentDeltaColDef(args, metric.id),
                ];
              } else {
                return [baseDef, ...comparisonValueCol];
              }
            })();
            const metricColDef = {
              headerName: s.displayName ? s.displayName : metric.name,
              marryChildren: true,
              flex: chartDef.gridSizing === 'fitContent' ? undefined : 1,
              // headerGroupComponent: metric ? MetricHeaderCell : undefined,
              headerGroupComponentParams: metric
                ? {
                    metric,
                    rightAlign: false,
                    alias: s.displayName,
                  }
                : undefined,
              children,
            };
            return [metricColDef, ...rankAndPercentageColDefs];
          } else {
            return [baseDef, ...rankAndPercentageColDefs];
          }
        }),
    ] as MatrixColDef[];
  }
};

export default buildMatrixColDefs;
